/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocation } from 'react-router';

import {
  CHAT_CONVERSATION_STORAGE_KEY,
  CHAT_DRAFT_MESSAGE_KEY,
  CHAT_SIDEBAR_STORAGE_KEY,
  CHAT_SIDEBAR_WIDTH_KEY,
} from '@/contexts/ChatSidebarContext';
import { useChatSidebar } from '@/contexts/useChatSidebar';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/hooks/useChat';
import { useResizableSidebar } from '@/hooks/useResizableSidebar';
import { useIsDesktop } from '@/hooks/useViewportBreakpoints';
import { cn } from '@/lib/utils';

import {
  ContextSelectionItem,
  ContextSelectionProvider,
} from '@/components/chat/ContextSelectionContext';
import type { FileContext } from '@/components/chat/parseAttachedFiles';

import { ChatSidebarContent } from './ChatSidebarContent';
import { filterContextSelectionItems, isHiddenChatRoute } from './utils';

export const ChatSidebar = () => {
  const { user, isLoaded } = useAuth();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const isDesktop = useIsDesktop();

  const {
    isOpen,
    mode,
    closeChat,
    switchMode,
    sidebarWidth,
    setSidebarWidth,
    setCurrentConversationId,
    setDraftMessage,
    pendingNewConversation,
    pendingMessage,
    pendingSelectedProducts,
    pendingConversationTitle,
    clearPendingNewConversation,
    setPendingFocusInput,
    pageContext,
  } = useChatSidebar();
  const {
    conversations,
    currentConversationId,
    messages,
    isLoading,
    isResponding,
    pendingAssistantForMessageId,
    loadConversations,
    createConversation,
    switchConversation,
    sendMessage,
    setMessages,
    setConversations,
    stopResponding,
  } = useChat();

  const [isInitialized, setIsInitialized] = useState(false);
  const isDesktopResolved = useRef(false);

  const { isResizing, handleMouseDown, handleTouchStart } = useResizableSidebar(
    {
      onWidthChange: setSidebarWidth,
    }
  );

  useEffect(() => {
    if (!isDesktopResolved.current) {
      isDesktopResolved.current = true;
      return;
    }
    if (!isDesktop && mode === 'sidebar') {
      switchMode('floating');
    }
  }, [isDesktop, mode, switchMode]);

  useEffect(() => {
    if (!isHiddenChatRoute(pathname) || !isOpen) {
      return;
    }

    closeChat();
  }, [pathname, isOpen, closeChat]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (user && isOpen && !isInitialized) {
      loadConversations();
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setIsInitialized(true);
    }
  }, [user, isOpen, isInitialized, loadConversations]);

  useEffect(() => {
    if (isLoaded && !user) {
      closeChat();
      setIsInitialized(false);

      setCurrentConversationId(null);
      setDraftMessage('');
      setMessages([]);
      setConversations([]);

      try {
        sessionStorage.removeItem(CHAT_CONVERSATION_STORAGE_KEY);
        sessionStorage.removeItem(CHAT_DRAFT_MESSAGE_KEY);
        sessionStorage.removeItem(CHAT_SIDEBAR_STORAGE_KEY);
        sessionStorage.removeItem(CHAT_SIDEBAR_WIDTH_KEY);
      } catch (error) {
        console.warn('Failed to clear chat storage on logout:', error);
      }
    }
  }, [
    isLoaded,
    user,
    closeChat,
    setCurrentConversationId,
    setDraftMessage,
    setMessages,
    setConversations,
  ]);

  const handleSendMessage = useCallback(
    async (
      message: string,
      contextSelection?: ContextSelectionItem[],
      fileContexts?: FileContext[]
    ) => {
      try {
        const selectedProducts = filterContextSelectionItems(contextSelection);

        if (!currentConversationId) {
          const conversationId = await createConversation();
          if (!conversationId) {
            return false;
          }
          await sendMessage(message, {
            conversationId,
            pageContext,
            selectedProducts,
            fileContexts,
          });
        } else {
          await sendMessage(message, {
            conversationId: currentConversationId,
            pageContext,
            selectedProducts,
            fileContexts,
          });
        }
        return true;
      } catch (error) {
        console.error('Error sending message:', error);
        toast({
          title: 'Failed to send message',
          description:
            'There was an error sending your message. Please try again.',
          variant: 'destructive',
        });
        return false;
      }
    },
    [currentConversationId, createConversation, sendMessage, toast, pageContext]
  );

  useEffect(() => {
    if (!isOpen || !pendingNewConversation || !pendingMessage) {
      return;
    }

    clearPendingNewConversation();
    const message = pendingMessage;
    const productsOverride = pendingSelectedProducts;
    const titleOverride = pendingConversationTitle;
    setDraftMessage('');

    (async () => {
      const conversationId = await createConversation(titleOverride);
      if (!conversationId) {
        setDraftMessage(message);
        return;
      }
      try {
        await sendMessage(message, {
          conversationId,
          selectedProductsOverride: productsOverride,
          pageContext,
        });
      } catch {
        setDraftMessage(message);
      }
    })();
  }, [
    isOpen,
    pendingNewConversation,
    pendingMessage,
    pageContext,
    clearPendingNewConversation,
    setDraftMessage,
    createConversation,
    sendMessage,
    pendingSelectedProducts,
    pendingConversationTitle,
  ]);

  const handleNewConversation = useCallback(async () => {
    setPendingFocusInput(true);

    try {
      await createConversation();
    } catch (error) {
      console.error('Failed to create conversation', error);
      setPendingFocusInput(false);
    }
  }, [createConversation, setPendingFocusInput]);

  const handleSwitchMode = useCallback(() => {
    switchMode(mode === 'floating' ? 'sidebar' : 'floating');
  }, [mode, switchMode]);

  const showEmptyState = !currentConversationId || messages.length === 0;

  if (mode === 'floating' && !isOpen) {
    return null;
  }

  const isFloating = mode === 'floating';

  return (
    <ContextSelectionProvider>
      <div
        className={cn('flex flex-col fixed z-50', {
          'bottom-4 right-4 left-4 sm:right-6 sm:left-auto sm:bottom-6 max-h-[80vh] sm:w-[544px] sm:h-[75vh] bg-card border border-border rounded-2xl shadow-md transition-all duration-200':
            isFloating,
          'opacity-100 scale-100 translate-y-0': isFloating && isOpen,
          'opacity-0 scale-95 translate-y-2 pointer-events-none':
            isFloating && !isOpen,
          'inset-y-0 right-0 bg-card border-l shadow-lg transform transition-transform duration-200':
            !isFloating,
          'translate-x-0': !isFloating && isOpen,
          'translate-x-full': !isFloating && !isOpen,
        })}
        style={!isFloating ? { width: `${sidebarWidth}px` } : undefined}
        role="complementary"
        aria-label="Stella AI Assistant Chat"
      >
        <ChatSidebarContent
          conversations={conversations}
          currentConversationId={currentConversationId}
          messages={messages}
          showEmptyState={showEmptyState}
          isLoading={isLoading}
          isResponding={isResponding}
          pendingAssistantForMessageId={pendingAssistantForMessageId}
          mode={mode}
          onNewConversation={handleNewConversation}
          onConversationSelect={switchConversation}
          onSendMessage={handleSendMessage}
          onSwitchMode={handleSwitchMode}
          onClose={closeChat}
          onStopResponding={stopResponding}
        />

        {!isFloating && (
          <div
            className={cn(
              'absolute left-0 top-0 bottom-0',
              'w-1',
              'cursor-col-resize',
              'hover:bg-primary/20',
              'active:bg-primary/40',
              { 'bg-primary/40': isResizing }
            )}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            role="separator"
            aria-label="Resize sidebar"
            aria-orientation="vertical"
          />
        )}
      </div>
    </ContextSelectionProvider>
  );
};
